import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { alignItems, strCode } from "../../../../../../consts";
import { log, notificationService, t } from "../../../../../../services";
import { dateUtil } from "../../../../../../utils";
import {
  Checkbox,
  DateInput,
  DropdownInput,
  FlexContainers,
  Input,
  Note,
  Switcher,
  TimeInput,
  TooltipIcon,
  WrapperContainer
} from "../../../../../../components";

import { CheckboxLabel, Label } from "./styled";
import { SectionTitle, InputSectionWrapper } from "../../styled";
import { MessagePreview } from "../../../../components/MessagePreview/MessagePreview";

export const OverviewSection = forwardRef(
  ({ overviewSectionData, isEdit, hasOriginalNotification }, ref) => {
    const [campaignName, setCampaignName] = useState("");

    const [hasNotificationMessage, setHasNotificationMessage] = useState(false);
    const [selectedNotificationName, setSelectedNotificationName] = useState();

    const [startDate, setStartDate] = useState();
    const [startTime, setStartTime] = useState();

    const [hasPushNotificationDate, setHasPushNotificationDate] = useState(false);
    const [pushNotificationDate, setPushNotificationDate] = useState();
    const [pushNotificationTime, setPushNotificationTime] = useState();

    const [isValid, setIsValid] = useState(false);

    const [showErrors, setShowErrors] = useState(false);
    const [missingFields, setMissingFields] = useState({});

    const notifications = useSelector((store) => store.notifications?.notifications);
    const notificationOptions = useMemo(() => {
      return (notifications ?? [])?.map((notification) => {
        return { id: notification.notificationName, value: notification.notificationName };
      });
    }, [notifications]);

    const minPushNotificationTime = useMemo(() => {
      if (startDate && dateUtil.isSameDate(startDate, pushNotificationDate) && startTime) {
        return startTime;
      }

      if (pushNotificationDate && dateUtil.isToday(pushNotificationDate)) {
        return dateUtil.getUTCDate();
      }

      return null;
    }, [startDate, startTime, pushNotificationDate]);

    const minStartTime = useMemo(() => {
      if (startDate && dateUtil.isToday(startDate)) {
        return dateUtil.getUTCDate();
      }

      return null;
    }, [startDate, startTime, pushNotificationDate]);

    const getOverviewSectionData = useCallback(() => {
      return {
        campaignName,
        hasNotificationMessage,
        selectedNotificationName,
        startDate,
        startTime,
        hasPushNotificationDate,
        pushNotificationDate,
        pushNotificationTime
      };
    }, [
      campaignName,
      hasNotificationMessage,
      selectedNotificationName,
      startDate,
      startTime,
      hasPushNotificationDate,
      pushNotificationDate,
      pushNotificationTime
    ]);

    useEffect(() => {
      prepopulateData();

      return () => {
        resetFormData();
      };
    }, [overviewSectionData]);

    useEffect(() => {
      let valid = campaignName?.length >= 5 && !!startDate && !!startTime;

      if (hasNotificationMessage) {
        valid = valid && !!selectedNotificationName;
      }

      if (hasPushNotificationDate) {
        valid = valid && !!pushNotificationDate && !!pushNotificationTime;
      }

      const errorFields = {
        campaignName: campaignName?.length < 5,
        startDate: !startDate,
        startTime: !startTime,
        selectedNotificationName: hasNotificationMessage && !selectedNotificationName,
        pushNotificationDate: hasPushNotificationDate && !pushNotificationDate,
        pushNotificationTime: hasPushNotificationDate && !pushNotificationTime
      };

      setMissingFields(errorFields);

      setIsValid(valid);
    }, [
      campaignName,
      hasNotificationMessage,
      selectedNotificationName,
      startDate,
      startTime,
      hasPushNotificationDate,
      pushNotificationDate,
      pushNotificationTime
    ]);

    useEffect(() => {
      if (minStartTime && startTime && !isEdit) {
        const minDate = new Date(minStartTime);

        minDate.setSeconds(0);
        minDate.setMilliseconds(0);

        if (minDate.getTime() > startTime.getTime()) {
          setStartTime(null);
        }
      }

      if (minPushNotificationTime && pushNotificationTime && !isEdit) {
        const minDate = new Date(minPushNotificationTime);

        minDate.setSeconds(0);
        minDate.setMilliseconds(0);

        if (minDate.getTime() > pushNotificationTime.getTime()) {
          setPushNotificationTime(null);
        }
      }
    }, [minPushNotificationTime, minStartTime, startTime, pushNotificationTime, isEdit]);

    const prepopulateData = useCallback(() => {
      if (overviewSectionData) {
        setCampaignName(overviewSectionData?.campaignName);

        setHasNotificationMessage(overviewSectionData?.hasNotificationMessage);
        setSelectedNotificationName(overviewSectionData?.selectedNotificationName);

        setStartDate(overviewSectionData?.startDate);
        setStartTime(overviewSectionData?.startTime);

        setHasPushNotificationDate(overviewSectionData?.hasPushNotificationDate);

        setPushNotificationDate(overviewSectionData?.pushNotificationDate);
        setPushNotificationTime(overviewSectionData?.pushNotificationTime);
      }
    }, [overviewSectionData]);

    const resetFormData = () => {
      setCampaignName("");

      setHasNotificationMessage(false);
      setSelectedNotificationName(null);

      setStartDate(null);
      setStartTime(null);

      setHasPushNotificationDate(false);

      setPushNotificationDate(null);
      setPushNotificationTime(null);

      setIsValid(true);
      setShowErrors(false);
      setMissingFields({});
    };

    useImperativeHandle(ref, () => ({
      saveOverviewSectionData(validate = true) {
        if (!showErrors && validate) {
          setShowErrors(true);
        }

        if (!isValid && validate) {
          log.warn("OverviewSection: Unable to save overview section due to missing fields");

          notificationService.showError({
            title: t(strCode.MISSING_FIELDS),
            message: t(strCode.MISSING_FIELDS_MESSAGE)
          });
          return;
        }

        return getOverviewSectionData();
      },
      resetOverviewSection() {
        resetFormData();
      }
    }));

    return (
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        gap={16}
        width={"100%"}>
        <SectionTitle>{t(strCode.OVERVIEW)}</SectionTitle>

        <InputSectionWrapper mt={8}>
          <Input
            id={"campaignName"}
            title={t(strCode.CAMPAIGN_NAME_TITLE)}
            placeholder={t(strCode.CAMPAIGN_NAME_TITLE)}
            warningMsg={t(strCode.CAMPAIGN_NAME_WARNING)}
            value={campaignName}
            setValue={setCampaignName}
            maxLength={36}
            hasError={showErrors && missingFields?.campaignName}
            disabled={isEdit}
          />

          <WrapperContainer
            Container={FlexContainers.FlexColumnStart}
            alignItems={alignItems.start}
            width={"100%"}>
            <WrapperContainer Container={FlexContainers.FlexRowStart} alignItems={alignItems.center}>
            <Label for={"notification-switcher"}>{t(strCode.NOTIFICATION_MESSAGE_TITLE)}</Label>
            <WrapperContainer Container={FlexContainers.FlexColumnStart} mb={8} pl={5}>
              <TooltipIcon>{t(strCode.NOTIFICATIONS_MESSAGE_HINT)}</TooltipIcon>
            </WrapperContainer>
          </WrapperContainer>

            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={16} width={"100%"}>
              <Switcher
                id={"notification-switcher"}
                checked={hasNotificationMessage}
                disabled={hasOriginalNotification}
                onSwitch={(value) => {
                  setHasNotificationMessage(value);

                  if (!value) {
                    setHasPushNotificationDate(false);
                    setSelectedNotificationName(null);
                  }
                }}
              />

              <DropdownInput
                id="notification-selector"
                placeholder={t(strCode.SELECT_PLACEHOLDER)}
                options={notificationOptions}
                selectedOption={selectedNotificationName}
                onSelectOption={(id) => {
                  setSelectedNotificationName(notificationOptions.find((opt) => opt.id === id));
                }}
                hasError={showErrors && missingFields?.selectedNotificationName}
                disabled={!hasNotificationMessage}
              />
            </WrapperContainer>
          </WrapperContainer>
        </InputSectionWrapper>

        {selectedNotificationName && (
          <MessagePreview
            notification={notifications?.find(
              (notification) => notification.notificationName === selectedNotificationName.id
            )}
          />
        )}

        <InputSectionWrapper>
          <DateInput
            id={"start-date"}
            title={t(strCode.START_DATE_TITLE)}
            value={startDate}
            setValue={(value) => {
              setStartDate(value);

              if (pushNotificationDate && value > pushNotificationDate) {
                setPushNotificationDate(value);
              }
            }}
            minDate={dateUtil.getUTCDate()}
            hasError={showErrors && missingFields?.startDate}
            noTime
          />

          <TimeInput
            id={"start-time"}
            title={t(strCode.START_TIME_TITLE)}
            value={startTime}
            setValue={setStartTime}
            minDate={minStartTime}
            hasError={showErrors && missingFields?.startTime}
          />
        </InputSectionWrapper>

        <InputSectionWrapper mb={8}>
          <Checkbox
            id={"push-notification-date-checkbox"}
            checked={hasPushNotificationDate}
            onCheck={(e) => {
              e.stopPropagation();
              setHasPushNotificationDate((prev) => !prev);
            }}
            disabled={!hasNotificationMessage}
          />

          <CheckboxLabel for={"push-notification-date-checkbox"} disabled={!hasNotificationMessage}>
            {t(strCode.PUSH_NOTIFICATION_CHECKBOX_LABEL)}
          </CheckboxLabel>
        </InputSectionWrapper>

        {hasPushNotificationDate && (
          <InputSectionWrapper>
            <DateInput
              id={"push-notification-date"}
              title={t(strCode.PUSH_NOTIFICATION_DATE_TITLE)}
              value={pushNotificationDate}
              setValue={setPushNotificationDate}
              minDate={startDate || dateUtil.getUTCDate()}
              hasError={showErrors && missingFields?.pushNotificationDate}
              noTime
            />

            <TimeInput
              id={"push-notification-time"}
              title={t(strCode.PUSH_NOTIFICATION_TIME_TITLE)}
              value={pushNotificationTime}
              setValue={setPushNotificationTime}
              minDate={minPushNotificationTime}
              hasError={showErrors && missingFields?.pushNotificationTime}
            />
          </InputSectionWrapper>
        )}

        <Note>
          {t(
            hasOriginalNotification
              ? strCode.NOTIFICATION_CAMPAIGN_UPDATE_NOTE
              : strCode.NOTIFICATION_CAMPAIGN_NOTE
          )}
        </Note>
      </WrapperContainer>
    );
  }
);

OverviewSection.displayName = "OverviewSection";

OverviewSection.propTypes = {
  overviewSectionData: {
    campaignName: PropTypes.string.isRequired,
    hasNotificationMessage: PropTypes.bool.isRequired,
    selectedNotificationName: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }),
    startDate: PropTypes.instanceOf(Date).isRequired,
    startTime: PropTypes.instanceOf(Date).isRequired,
    hasPushNotificationDate: PropTypes.bool.isRequired,
    pushNotificationDate: PropTypes.instanceOf(Date),
    pushNotificationTime: PropTypes.instanceOf(Date)
  },
  isEdit: PropTypes.bool.isRequired,
  hasOriginalNotification: PropTypes.bool.isRequired
};
